/**
 * EthosBoard website content (Home page). Source: Home v3 mockup + Homepage SEO review (Oct 15, 2026).
 * Copy is approved by Satya — edit text here, not in the JSX.
 */


export const NAV_LINKS = [
  // `also`: extra routes where this link counts as the current page
  { label: 'Platform', to: '/platform' },
  { label: 'Industries', to: '/industries' },
  { label: 'Pricing', to: '/pricing' },
  {
    label: 'Resources',
    to: '/resources',
    children: [
      { label: 'Skills framework', to: '/resources/skills-framework', desc: 'Roles, skills and proficiency' },
      { label: 'Interaction gallery', to: '/resources/interaction-gallery', desc: 'Real learner interactions' },
      { label: 'Report gallery', to: '/resources/report-gallery', desc: 'Dashboards leaders use' },
      { label: 'Guides and glossary', to: '/resources/guides', desc: 'Plain language answers' },
      { label: 'Comparisons', to: '/resources/comparisons', desc: 'EthosBoard vs LMS and LXP' },
      // { label: 'Client stories', to: '/resources/client-stories', desc: 'How teams prove readiness' },
    ],
  },
  { label: 'Company', to: '/company' },
];

export const HERO = {
  eyebrow: 'Workforce readiness platform',
  title: 'The AI-powered LMS that turns learning into proven capability',
  subtitle:
    'Connect roles, skills, learning, and assessments so you can see who is ready, close the gaps, and prove it. Launch, sell, manage, and scale digital learning from one platform.',
  primaryCta: 'Book a demo',
  secondaryCta: 'Explore the Resources',
  trustedLabel: 'Trusted by enterprise teams in',
  industries: [
    'Pharmaceuticals', 'Retail', 'Aviation', 'Financial services',
    'Manufacturing', 'Oil, Gas and Energy', 'Logistics', 'IT and Software',
  ],
  readiness: { label: 'TEAM READINESS', grade: 'GRADE B', score: 82, note: '3 skills below target. EthosAI has drafted nudges for 7 learners.' },
};

export const MEET = {
  eyebrow: 'Meet EthosBoard',
  intro:
    'EthosBoard goes beyond a standard LMS, LXP, or content library. One login, one catalog, and one source of truth for learning and readiness data. Illustrative data.',
  assignLabel: 'Assign learning',
  cta: 'Explore the platform',
  sideNav: ['Today', 'People', 'Learning', 'Skills and roles', 'Outcomes', 'Community', 'EthosAI'],
  greeting: 'Good morning, Amal',
  org: 'Gulf Operations · 1,240 learners',
  kpis: [
    { label: 'READINESS', value: '82', suffix: 'B' },
    { label: 'AT RISK', value: '41' },
    { label: 'DUE THIS WEEK', value: '128' },
  ],
  teams: [
    { name: 'Operations', score: 91 },
    { name: 'Quality', score: 84 },
    { name: 'Partners', score: 76 },
    { name: 'Retail', score: 69 },
  ],
  briefingTitle: 'EthosAI morning briefing',
  briefing: [
    { t: 'Retail is 13 points below target', d: 'Fire safety refresher overdue for 22 people' },
    { t: '7 nudges ready to send', d: 'One per stalled learner, ready for your review' },
    { t: '2 deadline exceptions', d: 'Waiting on your approval' },
  ],
};

export const JOBS = {
  eyebrow: 'One platform, four jobs',
  title: 'Launch, sell, manage, and scale',
  items: [
    { verb: 'Launch', text: 'learning programs in days, with an AI course builder and support for SCORM content.' },
    { verb: 'Sell', text: 'courses and academies to customers, partners, and members.' },
    { verb: 'Manage', text: 'learners, approvals, and compliance with clear admin controls.' },
    { verb: 'Scale', text: 'from a first cohort to a global workforce, on the deployment model that fits you.' },
  ],
  builder: { tag: 'AI COURSE BUILDER', from: 'DRAFT FROM SAFETY-MANUAL.PDF', lines: ['1. Permit to work basics', '2. Gas testing in practice'], foot: '+ 6 knowledge checks · awaiting review' },
  sell: { tag: 'PARTNER ACADEMY', name: 'Certified installer', meta: '$149 · 6 modules' },
  approvalsTag: 'APPROVALS · 3 PENDING',
  approvals: [
    { t: 'New path: Store opening', d: 'Proposed by Sara, Retail manager' },
    { t: 'Deadline exception', d: 'Omar K. · 5 extra days' },
    { t: 'Course sign-off', d: 'Forklift safety · 4 learners' },
  ],
  scaleTag: 'ONE FOUNDATION',
  scaleChips: ['Arabic', 'English', 'Hindi', 'French', 'UAE region', 'EU region', 'Shared SaaS', 'On-premises', 'Standalone code'],
};

export const ROLES = {
  title: 'Made for learners, managers, and admins alike',
  subtitle: 'Four role views on one platform, each scoped to what that person needs to see and do.',
  items: [
    { role: 'Learner', icon: 'book', does: 'Works from a personal Today screen with an AI Learning Buddy, and tracks points, badges, certificates and skills.' },
    { role: 'Manager', icon: 'users', does: 'Sees team readiness, assigns learning, handles escalations and signs off completions.' },
    { role: 'Organization Admin', icon: 'layout', does: 'Runs people, learning, community, outcomes and EthosAI settings for one organization.' },
    { role: 'Super Admin', icon: 'globe', does: 'Oversees multiple organizations, licenses and platform-wide readiness.' },
  ],
};

export const DEPLOY = {
  eyebrow: 'Integrations and deployment',
  title: ['Connect the systems you already run.', 'Deploy it the way you need.'],
  stackTop: ['Microsoft Entra ID', 'Google Workspace', 'Okta', 'SAP SuccessFactors', 'Workday', 'Oracle HCM', 'Darwinbox', 'Zoho People'],
  stackBottom: ['BambooHR', 'Stripe', 'PayPal', 'Razorpay', 'LinkedIn Learning', 'Coursera', 'Udemy Business', 'Go1'],
  stackTitle: 'Plays well with your stack',
  stackText:
    'SCORM plays today in the built-in player. SSO, HRMS, payment and content library connectors are available by plan or as add-ons.',
  planLink: 'See what each plan includes',
  deployTitle: 'Secured by you',
  options: [
    { icon: 'globe', name: 'Shared SaaS', desc: 'Start fast with no infrastructure to maintain.' },
    { icon: 'shield', name: 'On-premises', desc: 'Self hosted, so data stays inside your own environment.' },
    { icon: 'layout', name: 'Standalone code', desc: 'Full ownership and control of the platform.' },
  ],
};

export const RESOURCES = {
  eyebrow: 'Resources',
  title: 'Explore EthosBoard before you book a demo',
  hint: 'Hover to pause',
  items: [
    { icon: 'target', name: 'Skills framework', desc: 'Roles, skills and proficiency' },
    { icon: 'eye', name: 'Interaction gallery', desc: 'Real learner interactions' },
    { icon: 'layout', name: 'Report gallery', desc: 'Dashboards leaders use' },
    { icon: 'book', name: 'Guides and glossary', desc: 'Plain language answers' },
    { icon: 'check', name: 'Comparisons', desc: 'EthosBoard vs LMS and LXP' },
    // { icon: 'award', name: 'Client stories', desc: 'How teams prove readiness' },
  ],
};

import oilGas from '../../assets/images/eb/industries/oil_gas_industry.png';
import pharma from '../../assets/images/eb/industries/health_pharma.png';
import bfsi from '../../assets/images/eb/industries/bfsi.png';
import manufacturing from '../../assets/images/eb/industries/manufacturing.png';
import aviation from '../../assets/images/eb/industries/aviation.png';


export const INDUSTRIES = {
  eyebrow: 'Industries',
  title: ['Built for the rules of your industry.', 'Ready before the audit.'],
  // To use real photos: import each image at the top of this file and add `photo: yourImport` to its item.
  items: [
    { wave: 'WAVE 1', name: 'Oil, Gas and Energy', photo: oilGas, line: 'Record competence, not attendance, for crews on rotation.' },
    { wave: 'WAVE 1', name: 'Healthcare and Pharma', photo: pharma, line: 'Audit-ready GxP training evidence without the spreadsheet chase.' },
    { wave: 'WAVE 1', name: 'BFSI', photo: bfsi, line: 'Role-based AML and conduct training with proof of comprehension.' },
    { wave: 'WAVE 2', name: 'Manufacturing', photo: manufacturing, line: 'Skills matrices by line, machine and shift.' },
    { wave: 'WAVE 2', name: 'Aviation and Aerospace', photo: aviation, line: 'Continuous evidence of competence for certifying staff.' },
  ],
  allLabel: 'See all eight industries',
};

export const COMPARE = {
  title: 'Completion is not readiness. It is where we start.',
  subtitle: 'A course marked complete tells you someone finished a module. EthosBoard tells you whether they can do the job.',
  colA: 'Typical LMS or LXP',
  colB: 'EthosBoard',
  rows: [
    { k: 'Starts from', a: 'The course catalog', b: 'The role, and the skills it requires' },
    { k: 'Measures', a: 'Completions and engagement', b: 'A workforce readiness score, graded A to D' },
    { k: 'AI', a: 'Recommends more content', b: 'EthosAI drafts courses, briefings and nudges, with your approval' },
    { k: 'Content', a: 'Stores and delivers it', b: 'SCORM, AI course builder, and Infylearn content services' },
    { k: 'Selling learning', a: 'A separate tool', b: 'Catalogs, pricing and branding per audience, built in' },
    { k: 'Deployment', a: 'Vendor cloud only', b: 'Shared SaaS, on-premises, or standalone code' },
  ],
};

export const MOBILE = {
  eyebrow: 'Mobile app · iOS and Android',
  title: 'Readiness in every pocket',
  text:
    'One app for every role. Learners take SCORM courses on the go. Managers and admins monitor progress, approve requests, and ask EthosAI for insights.',
  team: [
    { n: 'Aisha R.', s: 92 },
    { n: 'Omar K.', s: 71 },
    { n: 'Priya S.', s: 84 },
    { n: 'Daniel M.', s: 58 },
  ],
};

import partnerPhoto from '../../assets/images/landing/team_at_work.png';

export const PARTNER = {
  title: 'A content partner behind the product',
  text1: 'Built by Infylearn Technologies, a digital learning company in Sharjah and Mumbai.',
  text2:
    'Custom eLearning, video based learning, instructor led and virtual training, gamification, AR, VR, and XR experiences, and translation and localization are available alongside the platform whenever you need them.',
  link: 'Meet the company',
  photoCaption: 'Photo: Infylearn instructional design team at work',
  photoAlt: 'Infylearn instructional design team at work',
  photo: partnerPhoto,
};

export const FAQS = [
  ['What is a workforce readiness platform?', 'A workforce readiness platform shows, with evidence, whether people are prepared for their roles, regulations, and changes in the business. EthosBoard does this by linking roles and skills to learning, assessments, and a workforce readiness score.'],
  ['What is EthosBoard used for?', 'EthosBoard is used to create, deliver, sell, and track digital learning. Organizations use it to train employees, onboard partners, serve customers, and measure workforce readiness from one platform.'],
  ['Is EthosBoard an LMS, an LXP, or a content library?', 'EthosBoard has a full LMS and LXP built in, but it is designed as a workforce readiness platform. Its purpose is to show that your people are ready for their roles. That is why it adds a skills framework, EthosAI, and a workforce readiness score.'],
  ['What is the workforce readiness score?', 'The workforce readiness score is an EthosBoard measure that combines learning activity, assessments, and role requirements to show how prepared individuals and teams are for their work.'],
  ['Does EthosBoard support SCORM content?', 'Yes. EthosBoard is a SCORM compliant LMS, so you can bring existing courses onto the platform, and SCORM courses can be played in the mobile app.'],
  ['Can learners use EthosBoard on their phones?', 'Yes. One mobile app for iOS and Android serves every role. Learners complete their learning in the app, while admins monitor progress and approve requests.'],
  ['How can EthosBoard be deployed?', 'EthosBoard can be deployed as shared SaaS, on-premises as a self hosted LMS, or as standalone code, depending on your size, security needs, and ownership preferences.'],
  ['Who builds EthosBoard?', 'EthosBoard is built by Infylearn Technologies, a digital learning company with offices in Sharjah, UAE and Mumbai, India.'],
].map(([q, a]) => ({ q, a }));

export const DEMO_CTA = {
  title: 'See EthosBoard in action',
  text: 'Book a 30 minute walkthrough and see how EthosBoard can help you prove your workforce is ready.',
  explore: 'Prefer to explore first? Start with the',
  // links: ['Skills framework', 'Report gallery', 'Client stories'],
  links: ['Skills framework', 'Report gallery'],
  button: 'Book a demo',
};

export const FOOTER_COLUMNS = [
  { title: 'Platform', items: [
    { label: 'Skills and roles', href: '/platform' }, { label: 'Learning and delivery', href: '/platform' },
    { label: 'EthosAI', href: '/platform#ethosai' }, { label: 'Mobile app', href: '/#mobile' }, { label: 'Deployment', href: '/#deploy' } ] },
  { title: 'Industries', items: [
    { label: 'Oil, Gas and Energy', href: '/#industries' }, { label: 'Healthcare and Pharma', href: '/#industries' },
    { label: 'BFSI', href: '/#industries' }, { label: 'Manufacturing', href: '/#industries' },
    { label: 'Aviation and Aerospace', href: '/#industries' } ] },
  { title: 'Company', items: [
    { label: 'About Infylearn', href: '/#partner' }, { label: 'FAQ', href: '/faq' },
    { label: 'Contact', href: '/enquiry' }, { label: 'Book a demo', href: '/enquiry' } ] },
];